import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import update from 'lodash/update';
import { arrayOf, bool, func, number, object, oneOfType, shape, string } from 'prop-types';
import { capitalizeFirstLetter } from 'ps-components/lib/formatters';
import { getPdfSubsetIndex } from './payElementMappings';
import CompensationSummaryConfig from './CompensationSummaryConfig/CompensationSummaryConfig';
import CompensationSummaryHeader from './CompensationSummaryHeader/CompensationSummaryHeader';
import EmployeeData from './EmployeeData/EmployeeData';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import InternalData from './InternalData/InternalData';
import InternalJobDescription from './InternalJobDescription/InternalJobDescription';
import JobNotPricedCard from './JobNotPricedCard/JobNotPricedCard';
import MarketRange from './MarketRange/MarketRange';
import CompensationSummaryPricingDetails from './PricingDetails/CompensationSummaryPricingDetails';
import SurveyJobDescription from './SurveyJobDescription/SurveyJobDescription';
import Disclosure from '../Disclosure/Disclosure';
import { hideModal } from '../../actions/modal';
import { clearSelectedJobs, setSelectedJob } from '../JobMatching/OrgJobsActions';
import { resetReportDropdown } from './../MarketPricing/MarketPricingActions';
import * as compensationSummaryActionCreators from './CompensationSummaryActions';
import './CompensationSummaryContainer.scss';
import { LINK_HASH_STRUCTURE_MODEL_ANALYTICS } from 'ps-components/Slideout/SlideoutAddresses';
import { withRouter } from 'react-router';
import { compensationSummaryJobPricingGridActions } from '../MarketPricing/JobPricingDetails/JobPricingGrid/CompensationSummaryJobPricingGridCreate';
import { PRICING_TYPE } from '../SkillGeoDifferential/SkillGeoDifferentialReducer';
import classnames from 'classnames';
import OverallComments from './OverallComments/OverallComments';
import { getColumnIndex, getColumnValue } from 'ps-components/AdvancedTable/GenericGrid/GenericGridHelper';
import CompensationSummaryParticipationMatchDetails from './ParticipationMatchDetails/CompensationSummaryParticipationMatchDetails';
import * as participationMatchesActions from '../MarketPricing/ParticipationMatches/ParticipationMatchesActions';
import { participationMatchesGridActions } from '../MarketPricing/ParticipationMatches/ParticipationMatchesGrid/ParticipationMatchesGridCreate';
import { addMatch, deleteMatch } from '../MarketPricing/MarketPricingActions';
import { addSelectedCutID, getJobPricingDetails } from '../MarketPricing/JobPricing/JobPricingActions';
import * as surveyJobsActionCreators from '../JobMatching/SurveyJobsActions';
import { isAdHocJobId } from '../MarketPricing/helper';

class CompensationSummaryContainer extends React.Component {
  static propTypes = {
    actions: shape({
      getJobEmployees: func,
      getJobTopView: func,
      toggleReportSection: func,
      setSelectedJob: func,
      setViewName: func,
      setSurveyViewName: func
    }),
    data: shape({
      employeeData: object,
      internalData: arrayOf(
        shape({
          key: string,
          value: oneOfType([number, string])
        })
      ),
      marketRangeData: shape({
        '25th': number,
        '50th': number,
        '75th': number
      }),
      slottedJobs: arrayOf(
        shape({
          slotJobCode: string,
          slotJobTitle: string,
          slotJobCodeKey: string
        })
      ),
      pricingType: string
    }),
    isConfigVisible: bool,
    loading: bool,
    organization: shape({
      clientManager: string,
      code: string,
      defaultGroup: shape({
        id: string,
        name: string
      }),
      id: string,
      implemenationManager: string,
      logo: string,
      name: string,
      useJobCodeKey: bool,
      masterAccountId: oneOfType([number, string])
    }),
    replacementTokens: arrayOf(
      shape({
        token: string,
        value: string
      })
    ),
    displayIn: string,
    selectedJob: shape({
      code: string,
      codeKey: string,
      description: string,
      id: oneOfType([number, string]),
      title: string
    }),
    selectedJobs: arrayOf(
      shape({
        id: number,
        code: string,
        codeKey: string
      })
    ),
    showEmployeeData: bool,
    showInternalData: bool,
    showInternalJobDescription: bool,
    showLastModified: bool,
    showMarketComparison: bool,
    showMarketRange: bool,
    showMatchDetails: bool,
    showZeroWeightMatches: bool,
    showParticipationMatchesWithData: bool,
    showParticipationMatchesWithoutData: bool,
    showOverallComments: bool,
    showOverallComposites: bool,
    showSurveyJobDescription: bool,
    showCoverPage: bool,
    showCompanyLogo: bool,
    showPayScaleLogo: bool,
    customFooter: string,
    customHeader: string,
    reportName: string,
    showPricingDetails: bool,
    isDownloading: bool,
    csrfPair: object,
    showJobCodeKey: bool,
    monetaryRounding: number,
    percentRounding: number,
    numericRounding: number,
    selectedBenchmarkYear: number,
    pricingType: string,
    match: object,
    siteSettings: shape({
      showAgingFactor: bool,
      showWgtBy: bool,
      useParticipationMatch: bool,
      showGeoAdjustment: bool
    }),
    jobPricing: shape({
      selectedAgingFactor: string,
      selectedViewPk: number
    }),
    jobPricingGridActions: object,
    jobPricingGrid: object,
    participationMatches: object,
    participationMatchesGridActions: object,
    participationMatchesActions: object,
    addMatch: func,
    viewName: string,
    surveyViewName: string,
    previousPath: string
  };

  constructor(props) {
    super(props);
    this.state = {
      showModifiedHeader: false,
      pricingDetailsFetched: false,
    };
  }

  componentDidMount() {
    const { actions, modal, modalProps, match } = this.props;

    if (match.params?.jobId) {
      actions.setSelectedJob(modalProps);
      const compSumBody = document.getElementById('comp-sum-content');
      if (compSumBody) compSumBody.addEventListener('scroll', e => this.scrollListener(e));
    }
    if (!match.params?.jobId) {
      const orgJob = modalProps && modalProps.orgJob ? modalProps.orgJob : modal.modalProps.orgJob;
      actions.setSelectedJob(orgJob);
      const compSumBody = document.getElementById('comp-sum-content');
      if (compSumBody) compSumBody.addEventListener('scroll', e => this.scrollListener(e));
    }
  }

  componentDidUpdate(prevProps) {
    const {
      actions,
      selectedJob,
      history,
      replacementTokens,
      selectedBenchmarkYear,
      displayIn,
      match,
      modalProps,
      jobPricingGridActions,
      organization,
      loading,
      participationMatchesGridActions,
      showZeroWeightMatches,
      userRights
    } = this.props;

    const hasSelectedJob = Object.keys(selectedJob).length > 1;
    const numberPattern = /\d+/g;
    const hasPatternMatch = location.pathname.match(numberPattern);

    const csrViewName = history?.location?.state?.modalProps?.viewName;
    const csrSurveyViewName = history?.location?.state?.modalProps?.surveyViewName;


    if (!hasSelectedJob && loading === false) actions.setLoading();

    if (hasSelectedJob && selectedJob.id !== prevProps.selectedJob.id) {
      actions.setViewName(csrViewName);
      actions.setSurveyViewName(csrSurveyViewName);
      if (!this.state.pricingDetailsFetched) {
        this.setState({ ...this.state, PricingDetailsFetched: true })
        actions.initCompSum(
          selectedJob,
          replacementTokens,
          selectedBenchmarkYear,
          displayIn,
          jobPricingGridActions.search,
          organization.masterAccountId,
          selectedJob?.currency || 'USD',
          csrViewName,
          csrSurveyViewName,
          participationMatchesGridActions.search,
          modalProps.selectedEmployeesViewPk,
          modalProps.selectedJobDetailsViewPk,
          userRights.restrictedUser,
          showZeroWeightMatches
        );
      }
    }

    if (prevProps.showZeroWeightMatches !== showZeroWeightMatches) {
      jobPricingGridActions.getJobPricingDetails(selectedJob, selectedBenchmarkYear, displayIn, jobPricingGridActions.search, organization.masterAccountId, csrViewName, showZeroWeightMatches)
    }

    if (this.props.history.action === 'POP' && prevProps.selectedJob && selectedJob.id !== prevProps.selectedJob.id) {
      if (this.props.history.location.pathname.includes('/market-analysis/jobs/')) {
        history.push(
          `/market-analysis/jobs/${match.params.jobId}/pricing/${modalProps.code}/compensation-summary-report`,
          {
            modal: true,
            modalType: 'COMPENSATION_SUMMARY_REPORT',
            modalProps: { className: 'comp-sum__modal', orgJob: selectedJob },
            component: 'COMPENSATION_SUMMARY_REPORT'
          }
        );
      }
    }

    if (this.props.history.action === 'PUSH' && prevProps.selectedJob && selectedJob.id !== prevProps.selectedJob.id) {
      if (this.props.history.location.pathname.includes('/market-analysis/rules-based-pricing/')) {
        history.push(`/market-analysis/rules-based-pricing/jobs/${selectedJob.id}/compensation-summary-report`, {
          modal: true,
          modalType: 'COMPENSATION_SUMMARY_REPORT',
          modalProps: { className: 'comp-sum__modal', orgJob: selectedJob },
          component: 'COMPENSATION_SUMMARY_REPORT'
        });
      } else if (this.props.history.location.pathname.includes('/iframe/merit/pricing2/')) {
        history.push(`/iframe/merit/pricing2/price/jobs/${selectedJob.id}/compensation-summary-report`, {
          modal: true,
          modalType: 'COMPENSATION_SUMMARY_REPORT',
          modalProps: {
            className: 'comp-sum__modal',
            orgJob: selectedJob,
            launchDarklyFeatureFlags: modalProps.launchDarklyFeatureFlags
          },
          component: 'COMPENSATION_SUMMARY_REPORT'
        });
      } else if (this.props.history.location.pathname.includes('/structure-modeling/jobs/')) {
        history.push(
          `/iframe/merit/structures/strucselect/structure-select/jobs/${selectedJob.id}/compensation-summary-report`,
          {
            modal: true,
            modalType: 'COMPENSATION_SUMMARY_REPORT',
            modalProps: { className: 'comp-sum__modal', orgJob: selectedJob },
            component: 'COMPENSATION_SUMMARY_REPORT'
          }
        );
      }
    }

    if (
      this.props.history.action === 'POP' &&
      !match.params?.jobId &&
      hasPatternMatch &&
      hasPatternMatch[0] !== prevProps.location.pathname.match(numberPattern)[0]
    ) {
      actions.setSelectedJob({ id: history.location.state.modalProps.orgJob.id });
    }

    if (this.props.loading !== prevProps.loading) {
      const compSumBody = document.getElementById('comp-sum-content');
      if (compSumBody) compSumBody.addEventListener('scroll', e => this.scrollListener(e));
    }
  }

  UNSAFE_componentWillUnmount() {
    const { clearSelectedJobs } = this.props.actions;
    clearSelectedJobs();
    const compSumBody = document.getElementById('comp-sum-content');
    if (compSumBody) compSumBody.removeEventListener('scroll', e => this.listener(e));
  }

  downloadReportPDF = (isBulkDownload, targetFileType) => {
    const {
      actions,
      csrfPair,
      customFooter,
      customHeader,
      displayIn,
      organization,
      reportName,
      selectedBenchmarkYear,
      selectedJob,
      selectedJobs,
      showCompanyLogo,
      showPayScaleLogo,
      surveyViewName,
      viewName,
      modalProps,
      showZeroWeightMatches
    } = this.props;

    if (isBulkDownload) {
      const jobs = selectedJobs.map(job => {
        return {
          orgJobId: job.id,
          orgJobCode: job.code,
          orgJobCodeKey: job.codeKey
        };
      });

      actions.bulkDownload(
        jobs,
        organization.logo,
        selectedBenchmarkYear,
        reportName,
        customHeader,
        customFooter,
        csrfPair,
        displayIn,
        showCompanyLogo,
        showPayScaleLogo,
        viewName,
        surveyViewName,
        modalProps.selectedEmployeesViewPk,
        modalProps.selectedJobDetailsViewPk,
        targetFileType,
        showZeroWeightMatches
      );
    } else {
      actions.downloadPDF(
        selectedJob.id,
        selectedJob.code,
        selectedJob.codeKey,
        organization.logo,
        selectedJob.title,
        selectedBenchmarkYear,
        reportName,
        customHeader,
        customFooter,
        csrfPair,
        displayIn,
        showCompanyLogo,
        showPayScaleLogo,
        viewName,
        surveyViewName,
        modalProps.selectedEmployeesViewPk,
        modalProps.selectedJobDetailsViewPk,
        showZeroWeightMatches
      );
    }
  };

  goToNextJob = () => {
    const { actions, selectedJob, selectedJobs } = this.props;
    const currentID = selectedJob.id;
    const selectedJobIDs = selectedJobs.map(job => job.id);
    const nextIndex = selectedJobIDs.indexOf(parseInt(currentID)) + 1;

    nextIndex === selectedJobIDs.length
      ? actions.setSelectedJob({ id: selectedJobs[0].id })
      : actions.setSelectedJob({ id: selectedJobs[nextIndex].id });
  };

  goToPreviousJob = () => {
    const { actions, selectedJob, selectedJobs } = this.props;
    const currentID = selectedJob.id;
    const selectedJobIDs = selectedJobs.map(job => job.id);
    const previousIndex = selectedJobIDs.indexOf(parseInt(currentID)) - 1;

    previousIndex < 0
      ? actions.setSelectedJob({ id: selectedJobs[selectedJobs.length - 1].id })
      : actions.setSelectedJob({ id: selectedJobs[previousIndex].id });
  };

  onToggle = id => {
    const {
      actions,
      showInternalData,
      showInternalJobDescription,
      showMarketRange,
      showMatchDetails,
      showZeroWeightMatches,
      showParticipationMatchesWithData,
      showParticipationMatchesWithoutData,
      showOverallComposites,
      showOverallComments,
      showSurveyJobDescription,
      showEmployeeData,
      showMarketComparison,
      showLastModified,
      showCoverPage,
      showCompanyLogo,
      showPayScaleLogo,
      showPricingDetails,
      customFooter,
      customHeader,
      reportName
    } = this.props;
    const currentSettings = {
      showInternalData,
      showInternalJobDescription,
      showMarketRange,
      showMatchDetails,
      showParticipationMatchesWithData,
      showParticipationMatchesWithoutData,
      showOverallComposites,
      showZeroWeightMatches,
      showOverallComments,
      showSurveyJobDescription,
      showEmployeeData,
      showMarketComparison,
      showLastModified,
      showCoverPage,
      showPricingDetails,
      showCompanyLogo,
      showPayScaleLogo,
      customFooter,
      customHeader,
      reportName
    };

    const idToChange = id === 'marketBaseRange' ? id.replace(/\W*(Base)\W*/, '') : id;
    const valueToChange = `show${capitalizeFirstLetter(idToChange)}`;

    actions.toggleReportSection(idToChange, { ...currentSettings, [valueToChange]: !currentSettings[valueToChange] });
  };

  saveConfigSettings = data => {
    const {
      actions,
      showInternalData,
      showInternalJobDescription,
      showMarketRange,
      showMatchDetails,
      showParticipationMatchesWithData,
      showParticipationMatchesWithoutData,
      showOverallComposites,
      showOverallComments,
      showZeroWeightMatches,
      showSurveyJobDescription,
      showEmployeeData,
      showMarketComparison,
      showLastModified,
      showCoverPage,
      showPricingDetails,
      showCompanyLogo,
      showPayScaleLogo
    } = this.props;
    const dataValues = Object.entries(data);
    const currentSettings = {
      customFooter: '',
      customHeader: '',
      reportName: '',
      showInternalData: showInternalData,
      showInternalJobDescription: showInternalJobDescription,
      showMarketRange: showMarketRange,
      showMatchDetails: showMatchDetails,
      showParticipationMatchesWithData: showParticipationMatchesWithData,
      showParticipationMatchesWithoutData: showParticipationMatchesWithoutData,
      showOverallComposites: showOverallComposites,
      showOverallComments: showOverallComments,
      showSurveyJobDescription: showSurveyJobDescription,
      showEmployeeData: showEmployeeData,
      showMarketComparison: showMarketComparison,
      showLastModified: showLastModified,
      showCoverPage: showCoverPage,
      showPricingDetails: showPricingDetails,
      showZeroWeightMatches,
      showCompanyLogo,
      showPayScaleLogo
    };

    dataValues.forEach(element =>
      update(currentSettings, element[0], () => {
        return element[1];
      })
    );

    actions.saveConfigSettings(data, currentSettings);
  };

  hideModal = () => {
    const { actions, history, ruleId, selectedJob, previousPath } = this.props;

    if (previousPath) {
      history.push(previousPath);
    } else if (history.location.pathname.includes('rules-based-pricing')) {
      history.push(`/market-analysis/rules-based-pricing/rules/${ruleId}`);
    } else if (history.location.pathname.includes('/iframe/merit/pricing2/price/')) {
      history.push('/iframe/merit/pricing2/price');
    } else if (history.location.pathname.includes('structures/struc/structures')) {
      history.push(
        `${this.props.location.pathname}${this.props.location.search}` + LINK_HASH_STRUCTURE_MODEL_ANALYTICS
      );
    } else if (selectedJob?.id === 0) {
      // back navigation fix for adhoc slotted jobs
      actions.resetReportDropdown();
      history.goBack();
    } else if (history.location.pathname.includes('/market-analysis/jobs/')) {
      history.push(`/market-analysis/jobs/${selectedJob.id}/pricing`);
      actions.resetReportDropdown();
    } else {
      history.goBack();
    }

    actions.hideModal();
  };

  scrollListener = e => {
    const compSumBody = document.getElementById('comp-sum-content');

    if (compSumBody.scrollTop > 0) this.setState({ showModifiedHeader: true });
    else this.setState({ showModifiedHeader: false });
  };

  render() {
    const {
      actions,
      data,
      isConfigVisible,
      loading,
      organization,
      participationMatches,
      replacementTokens,
      displayIn,
      selectedJob,
      selectedJobs,
      showEmployeeData,
      showInternalData,
      showInternalJobDescription,
      showLastModified,
      showMarketComparison,
      showMarketRange,
      showMatchDetails,
      showParticipationMatchesWithData,
      showParticipationMatchesWithoutData,
      showZeroWeightMatches,
      showOverallComposites,
      showSurveyJobDescription,
      surveyViewName,
      showJobCodeKey,
      monetaryRounding,
      percentRounding,
      numericRounding,
      showPricingDetails,
      selectedBenchmarkYear,
      siteSettings,
      jobPricing,
      jobPricingGridActions,
      jobPricingGrid,
      modalProps,
      userRights
    } = this.props;

    const pmMatches = participationMatches?.participationMatchesReducer?.surveyJobs?.jobs;
    const pmCountWithData = pmMatches?.filter(pm => pm.hasData);
    const pmCountWithoutData = pmMatches?.filter(pm => !pm.hasData);
    const showPMWithData = pmCountWithData?.length === 0 ? false : showParticipationMatchesWithData;
    const showPMWithoutData = pmCountWithoutData?.length === 0 ? false : showParticipationMatchesWithoutData;

    const { showModifiedHeader } = this.state;
    const isPageLoading =
      loading || Object.keys(selectedJob) < 1 || participationMatches?.participationMatchesReducer?.surveyJobs?.loading;

    if (isPageLoading) {
      return (
        <section className="compensation-summary__container">
          <div className="compensation-summary">
            <CompensationSummaryHeader
              hideModal={this.hideModal}
              isPageLoading={isPageLoading}
              benchmarkYear={selectedBenchmarkYear}
              useJobCodeKey={organization.useJobCodeKey}
              selectedEmployeesViewPk={modalProps.selectedEmployeesViewPk}
              selectedJobDetailsViewPk={modalProps.selectedJobDetailsViewPk}
              launchDarklyFeatureFlags={modalProps.launchDarklyFeatureFlags}
            />
            <InPlaceIndicator />
          </div>
        </section>
      );
    }

    const selectedJobIDs = selectedJobs.map(job => {
      return job.id;
    });
    const pricingType = jobPricingGrid?.metadata.pricingType;
    const isJobPriced = pricingType === PRICING_TYPE.PRICED;
    const isJobSlotted = pricingType === PRICING_TYPE.SLOTTED;
    const surveyWeightIndex = getColumnIndex(jobPricingGrid, 'data_weighting_factor_default');
    const benchmarkAdjustment = getColumnValue(jobPricingGrid, 'benchmark_adjustment_default');
    const benchmarkComment = getColumnValue(jobPricingGrid, 'benchmark_comment_default');
    const areAllWeightsZero =
      surveyWeightIndex !== -1 && jobPricingGrid.data.every(survey => survey[surveyWeightIndex] === 0);
    const isMatchedAndWeightedZero = (!benchmarkAdjustment || areAllWeightsZero) && jobPricingGrid.data.length >= 1;
    const pdfDataIndex = userRights.restrictedUser ? 1 : getPdfSubsetIndex(data.employeeData);
    const compSumContentClassNames = classnames('comp-sum-content', {
      modified: showModifiedHeader
    });

    const lastModified = getColumnValue(jobPricingGrid, 'last_modified');
    const lastModifiedUserName = getColumnValue(jobPricingGrid, 'last_mod_user_name');
    const agingDate = getColumnValue(jobPricingGrid, 'aging_date');

    /**  MPE-8258: Restricted users will have option to download an Excel hidden.
    actions.downloadToExcel hits a Merit endpoint (pricing2/one-page-summary-export) which should be updated
    to match restricted user expectations in a new ticket.
    */

    return (
      <section className="compensation-summary__container">
        <div className="compensation-summary">
          <CompensationSummaryHeader
            clearSelectedJobs={actions.clearSelectedJobs}
            dataAgedToDate={agingDate}
            downloadHandler={this.downloadReportPDF}
            downloadToExcel={actions.downloadToExcel}
            goToNextJob={this.goToNextJob}
            goToPreviousJob={this.goToPreviousJob}
            hideModal={this.hideModal}
            lastModifiedDate={lastModified}
            lastModifiedName={lastModifiedUserName}
            saveConfigSettings={this.saveConfigSettings}
            selectedJobIDs={selectedJobIDs}
            setSelectedBenchmarkYear={actions.setSelectedBenchmarkYear}
            toggleConfig={actions.toggleConfig}
            showModifedHeader={showModifiedHeader}
            useJobCodeKey={organization.useJobCodeKey}
            pricingType={pricingType}
            benchmarkYear={selectedBenchmarkYear}
            selectedEmployeesViewPk={modalProps.selectedEmployeesViewPk}
            selectedJobDetailsViewPk={modalProps.selectedJobDetailsViewPk}
            jobPricingGrid={jobPricingGrid}
            isRestrictedUser={userRights.restrictedUser}
            launchDarklyFeatureFlags={modalProps.launchDarklyFeatureFlags}
            showJobCodeKey={showJobCodeKey}
          />
          <div className={compSumContentClassNames} id="comp-sum-content">
            <CompensationSummaryConfig
              isConfigVisible={isConfigVisible}
              saveConfigSettings={this.saveConfigSettings}
              showLastModified={showLastModified}
            />
            <InternalData
              checked={showInternalData}
              isConfigVisible={isConfigVisible}
              data={data.internalData}
              onToggle={this.onToggle}
              replacementTokens={replacementTokens}
              hasData={!isAdHocJobId(selectedJob.id)}
            />
            <InternalJobDescription
              checked={showInternalJobDescription}
              isConfigVisible={isConfigVisible}
              data={selectedJob.description}
              onToggle={this.onToggle}
            />
            {(isJobPriced || isJobSlotted) &&
              !userRights.restrictedUser &&
              !isMatchedAndWeightedZero &&
              !isAdHocJobId(selectedJob.id) && (
                <MarketRange
                  checked={showMarketRange}
                  isConfigVisible={isConfigVisible}
                  id="market-range-chart"
                  data={data.marketRange}
                  onToggle={this.onToggle}
                  displayIn={displayIn}
                  monetaryRounding={monetaryRounding}
                />
              )}
            {(isJobPriced || isJobSlotted) && (
              <CompensationSummaryPricingDetails
                checked={showPricingDetails}
                onToggle={this.onToggle}
                isConfigVisible={isConfigVisible}
                siteSettings={siteSettings}
                saveConfigSettings={this.saveConfigSettings}
                showMarketComparison={showMarketComparison && !userRights.restrictedUser}
                showMatchDetails={showMatchDetails}
                showZeroWeightMatches={showZeroWeightMatches}
                showOverallComposites={showOverallComposites}
                hasEmployeeDataRows={data?.employeeData?.rowCount > 0}
                jobPricingGrid={jobPricingGrid}
                jobPricingGridActions={jobPricingGridActions}
                jobPricing={jobPricing}
                job={selectedJob}
                benchmarkYear={selectedBenchmarkYear}
                displayIn={displayIn}
                masterAccountId={organization.masterAccountId}
                aging={jobPricing?.selectedAgingFactor}
                loading={loading || jobPricingGrid.loading}
                selectedViewPk={jobPricing.selectedViewPk}
                useJobCodeKey={organization.useJobCodeKey}
                isRestrictedUser={userRights.restrictedUser}
              />
            )}
            {(isJobPriced || isJobSlotted) && (
              <OverallComments
                compositeComment={benchmarkComment}
                compositeAdjustment={benchmarkAdjustment}
                onToggle={this.onToggle}
              />
            )}
            {isJobPriced && (
              <SurveyJobDescription
                isConfigVisible={isConfigVisible}
                checked={showSurveyJobDescription}
                data={jobPricingGrid}
                onToggle={this.onToggle}
              />
            )}
            {!isJobPriced && !isMatchedAndWeightedZero && !isJobSlotted && (
              <JobNotPricedCard checked={false} isConfigVisible={isConfigVisible} onToggle={this.onToggle} />
            )}
            {(isJobPriced || isJobSlotted) && !userRights.restrictedUser && (
              <CompensationSummaryParticipationMatchDetails
                isConfigVisible={isConfigVisible}
                onToggle={this.onToggle}
                job={selectedJob}
                benchmarkYear={selectedBenchmarkYear}
                displayIn={displayIn}
                masterAccountId={organization.masterAccountId}
                selectedViewPk={jobPricing.selectedViewPk}
                actions={{
                  ...this.props.participationMatchesActions,
                  ...this.props.participationMatchesGridActions
                }}
                counts={{ data: 0, noData: 0 }}
                currency={selectedJobs[0]?.currency || 'USD'}
                filterText={''}
                grid={this.props.participationMatches.participationMatchesGridReducer}
                groupId={this.props.group.groupId}
                hideSelect={true}
                jobId={this.props.selectedJob.id}
                monetaryRounding={monetaryRounding}
                percentRounding={percentRounding}
                numericRounding={numericRounding}
                selectedSurveys={[]}
                showParticipationMatchesWithData={showPMWithData}
                showParticipationMatchesWithoutData={showPMWithoutData}
                showWithData={true}
                surveyJobs={this.props.participationMatches.participationMatchesReducer.surveyJobs}
                surveyViewName={surveyViewName}
              />
            )}
            {!userRights.restrictedUser && (
              <EmployeeData
                isConfigVisible={isConfigVisible}
                checked={showEmployeeData}
                data={data.employeeData}
                onToggle={this.onToggle}
                replacementTokens={replacementTokens}
                showJobCodeKey={showJobCodeKey}
                monetaryRounding={monetaryRounding}
                percentRounding={percentRounding}
                numericRounding={numericRounding}
              />
            )}
            {!!data?.employeeData?.rows?.length && data?.employeeData?.fields?.length > pdfDataIndex && (
              <Disclosure
                text={`PDF will only include first ${pdfDataIndex} fields of employee data.  To include all fields, download report as spreadsheet.`}
              />
            )}
          </div>
        </div>
      </section>
    );
  }
}

const mapStateToProps = ({ compensationSummary, session, rules, orgJobs, marketPricing, group }) => {
  return {
    ...compensationSummary,
    group,
    organization: session.organization,
    replacementTokens: session.replacementTokens,
    csrfPair: session.csrfPair,
    ruleId: rules.selectedRule.id,
    selectedJobs: orgJobs.jobs,
    selectedJob: orgJobs.selectedJob,
    siteSettings: {
      showAgingFactor: compensationSummary.showAgingFactor,
      showWgtBy: compensationSummary.showWgtBy,
      useParticipationMatch: compensationSummary.useParticipationMatch,
      showGeoAdjustment: compensationSummary.showGeoAdjustment
    },
    jobPricing: marketPricing.JobPricingReducer,
    jobPricingGrid: marketPricing.JobPricingReducer.CompensationSummaryGridReducer,
    participationMatches: marketPricing.ParticipationMatchesReducer,
    userRights: session.user.rights
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...compensationSummaryActionCreators,
      clearSelectedJobs,
      setSelectedJob,
      resetReportDropdown,
      hideModal,
      ...surveyJobsActionCreators,
      addMatch,
      addSelectedCutID,
      deleteMatch
    },
    dispatch
  ),
  jobPricingGridActions: bindActionCreators(
    {
      ...compensationSummaryJobPricingGridActions,
      getJobPricingDetails
    },
    dispatch
  ),
  participationMatchesActions: bindActionCreators(
    {
      ...participationMatchesActions
    },
    dispatch
  ),
  participationMatchesGridActions: bindActionCreators(
    {
      ...participationMatchesGridActions
    },
    dispatch
  )
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CompensationSummaryContainer));
